import {
	ControlledTreeEnvironment,
	Tree,
	InteractionMode,
} from "react-complex-tree";
import {
	Card,
	CardBody,
	Row,
	Col
} from "reactstrap";
import { useRef, useEffect, useState, useMemo } from "react";
import { treeTypes } from "../../constants/treeState";
import SearchInput from "../Search/SearchInput";
import { filterHierarchyBySearch } from "../../utilities/HierarchyOperations/filterHierarchyBySearch";
import { buildHasMoreAtDepthMap } from "../../utilities/HierarchyOperations/constructHierarchy";
import { createRenderTreeItem } from "../../utilities/HierarchyOperations/createRenderTreeItem";

const StructuralHierarchyTree = (
	{
		hierarchyData,
		handleRenderNode,
		getNodeLabel,
		handleExpand,
		handleCollapse,
		expandedItems,
		disabledItems,
		checkedItems,
		treeHeader,
		renderDepthOffset = 12,
		treeId,
		treeSize,
		actionButton,
		treeMaxHeight = "60vh"
	}) => {
	
	const treeRef = useRef();
	const scrollContainerRef = useRef(null);
	const hasActionButton = !!actionButton;
	const [searchTerm, setSearchTerm] = useState("");
	const [searchExpandedItems, setSearchExpandedItems] = useState([]);

	useEffect(() => {
		if (treeSize === treeTypes.Mini) {
			setTimeout(() => {
				// Find the first checked checkbox
				const checkedBox = document.querySelector(".mini-tree-checkbox:checked");

				if (checkedBox && scrollContainerRef.current) {
					checkedBox.scrollIntoView({ behavior: "smooth", block: "center" });
				}
			}, 100); // Small delay to ensure the tree is fully rendered
		}

	}, [treeSize]);

	// Filter hierarchy data based on search term
	const { filteredData, filteredExpandedItems } = useMemo(() => {
		return filterHierarchyBySearch(hierarchyData, searchTerm);
	}, [searchTerm, hierarchyData]);

	const effectiveExpandedItems = searchTerm
		? searchExpandedItems
		: expandedItems;
	// precompute rail info for the CURRENT filtered tree + expanded state
	const hasMoreAtDepthMap = useMemo(
		() => buildHasMoreAtDepthMap(filteredData, "root", effectiveExpandedItems),
		[filteredData, effectiveExpandedItems]
	);

	
	// Update expanded items when filter changes
	useEffect(() => {
		if (searchTerm) { setSearchExpandedItems(filteredExpandedItems); }
	}, [searchTerm, filteredExpandedItems]);

	const handleExpandWrapper = (item) => {
		if (searchTerm) { setSearchExpandedItems([...searchExpandedItems, item.index]); }
		else handleExpand(item);
	};

	const handleCollapseWrapper = (item) => {
		if (searchTerm) { setSearchExpandedItems(searchExpandedItems.filter(id => id !== item.index)); }
		else handleCollapse(item);
	};

	const indentSize = renderDepthOffset;
	const connectorGap = 3; // px gap between line tip and node
	// build the renderTreeItem once per dependency change
	const renderTreeItem = useMemo(
		() =>
			createRenderTreeItem({
				hasMoreAtDepthMap,
				handleRenderNode,
				indentSize,
				connectorGap,
			}),
		[hasMoreAtDepthMap, handleRenderNode, indentSize, connectorGap]
	);

	return (
		<ControlledTreeEnvironment
			items={filteredData}
			renderItem={renderTreeItem}
			getItemTitle={getNodeLabel}
			onExpandItem={handleExpandWrapper}
			onCollapseItem={handleCollapseWrapper}
			defaultInteractionMode={
				InteractionMode.DoubleClickItemToExpand
			}
			renderDepthOffset={renderDepthOffset}
			viewState={{
				[treeId]: {
					expandedItems: searchTerm ? searchExpandedItems : expandedItems,
					disabledItems,
					checkedItems,
				},
			}}
		>
			<Card className="mb-0 mt-0">
				{treeHeader}
				<CardBody>
					<Col md={6}>
						<SearchInput
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							onClear={() => setSearchTerm('')}
						/>
					</Col>
					<div className="scroll-container" ref={scrollContainerRef} style={{ maxHeight: treeMaxHeight, overflowY: "auto", margin: "0.2em", overflowX: 'hidden' }}>
						<Row style={{ marginLeft: "unset" }}>
							<Col md={hasActionButton ? 9 : 12}>
								<Tree
									treeId={treeId}
									rootItem="root"
									treeLabel="Structural Hierarchy"
									ref={treeRef}
								/>
							</Col>
							{hasActionButton && (
								<Col md={3}>
									{actionButton}
								</Col>
							)}
						</Row>
					</div>
				</CardBody>
			</Card>
		</ControlledTreeEnvironment>
	);
}

export default StructuralHierarchyTree;